import { useEffect, type RefObject } from 'react'

const ZERO = { position: '', left: '', top: '', zIndex: '', width: '', height: '', transformOrigin: '', transform: '' }

function applyStyle(element: HTMLElement, style: Partial<CSSStyleDeclaration>): void {
  Object.assign(element.style, style)
}

/**
 * Сканер на весь экран не должен зависеть от масштаба страницы. Если до открытия страница была приближена
 * (щипок, двойное касание), `position: fixed` занимает layout-вьюпорт, а на экране виден только его кусок:
 * рамка уезжает за край, затвор и подсказка остаются ниже экрана, а `touch-action: none` не даёт сдвинуть вид.
 * Пока масштаб не 1, ставим сканер ровно на видимую область: размер layout-вьюпорта, сдвиг и обратный масштаб.
 */
export function useUnzoomedFit(root: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const element = root.current
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport
    if (element === null || !viewport) return undefined

    const reset = () => applyStyle(element, ZERO)
    const fit = () => {
      const scale = viewport.scale
      if (!(scale > 0) || Math.abs(scale - 1) < 0.01) {
        reset()
        return
      }
      applyStyle(element, {
        position: 'fixed',
        left: '0',
        top: '0',
        zIndex: '1',
        width: `${viewport.width * scale}px`,
        height: `${viewport.height * scale}px`,
        transformOrigin: '0 0',
        transform: `translate(${viewport.offsetLeft}px, ${viewport.offsetTop}px) scale(${1 / scale})`,
      })
    }

    fit()
    viewport.addEventListener('resize', fit)
    viewport.addEventListener('scroll', fit)
    return () => {
      viewport.removeEventListener('resize', fit)
      viewport.removeEventListener('scroll', fit)
      reset()
    }
  }, [root])
}
